import { Filter } from '@/domain/analytics'
import {
  AnalyticsProduct,
  AnalyticsReturnReason,
  ProductProcess,
  ProductReturnRate,
  VariantReturnBreakdown
} from '@/domain/products'
import {
  CompensationAmounts,
  CountryReturnRate,
  CountryReturns,
  ItemsReturnedBreakdown,
  PeriodReturn,
  PeriodReturnRate,
  ReturnMetrics
} from '@/domain/returns'
import { formatPrice } from '@/utils'
import {
  DashboardanalyticspbAggregatedPeriodReturnRate,
  DashboardanalyticspbCountryReturnRateTableEntry,
  DashboardanalyticspbReturnsMainViewResponse,
  DashboardanalyticspbRefundsTypes,
  DashboardanalyticspbMoneyFormat,
  DashboardanalyticspbGetItemsAndTotalsPerDayResponse,
  DashboardanalyticspbReturnsCountryResponse,
  DashboardanalyticspbProductMainViewResponse,
  DashboardanalyticspbAnalyzedProduct,
  DashboardanalyticspbReason,
  DashboardanalyticspbReturnRateTableResponse,
  DashboardanalyticspbFindProductsResponse,
  DashboardanalyticspbProductDetailViewResponse,
  DashboardanalyticspbVariantBreakdowns,
  DashboardanalyticspbProductProcesses,
  DashboardanalyticspbReturnCountPerDay,
  DashboardanalyticspbAggregatedProductReturnRate,
  DashboardanalyticspbProductCountryReturnRate,
  DashboardanalyticspbGetAvailableFiltersResponse,
  DashboardanalyticspbScopePortal
} from '@itsrever/rever-api'
import moment from 'moment'

// The analytics API spells the money format's fields its own way and always writes the symbol last.
function formatAnalyticsPrice(
  price: number,
  format: DashboardanalyticspbMoneyFormat
): string {
  return formatPrice(price, {
    amount_multiplied_by: format.amount_multiplied,
    thousand_separator: format.thousand_separator,
    decimal_separator: format.decimal_separator,
    visible_number_of_decimals: format.visible_number_decimals,
    currency_symbol: format.currency_symbol
  })
}

export function toPeriodReturnRate(
  data: DashboardanalyticspbAggregatedPeriodReturnRate[]
) {
  const itemsPurchasedByMonth: number[] = []
  const itemsReturnedByMonth: number[] = []
  const itemsReturnedSplitByMonth: ItemsReturnedBreakdown[] = []
  const returnRatesByMonth: number[] = []
  const refundRatesByMonth: number[] = []
  const monthNames: string[] = []

  for (let i = 0; i < data.length; i++) {
    itemsPurchasedByMonth.push(Math.trunc(data[i].items_sold ?? 0))
    itemsReturnedByMonth.push(Math.trunc(data[i].items_returned ?? 0))
    itemsReturnedSplitByMonth.push({
      returned_refunds: Math.trunc(
        data[i].items_returned_split?.items_returned_refund ?? 0
      ),
      returned_gift_cards: Math.trunc(
        data[i].items_returned_split?.items_returned_store_credit ?? 0
      ),
      returned_exchanges: Math.trunc(
        data[i].items_returned_split?.items_returned_exchange ?? 0
      )
    })
    returnRatesByMonth.push(Math.trunc(data[i].return_rate ?? 0))
    refundRatesByMonth.push(Math.trunc(data[i].refund_rate ?? 0))
    monthNames.push(
      moment(data[i].period, 'YYYY-MM-DD').format('MMM, YY') ?? ''
    )
  }

  return {
    itemsPurchasedByMonth,
    itemsReturnedByMonth,
    itemsReturnedSplitByMonth,
    returnRatesByMonth,
    refundRatesByMonth,
    monthNames,
    shopCurrency: data[data.length - 1].currency_shop ?? 'EUR'
  }
}

export function toCountriesReturnRate(
  entries: DashboardanalyticspbCountryReturnRateTableEntry[]
): CountryReturnRate[] {
  const countryReturnRates: CountryReturnRate[] = []

  for (let i = 0; i < entries.length; i++) {
    countryReturnRates.push({
      country: entries[i].country ?? '',
      itemsPurchased: Math.trunc(entries[i].items_sold ?? 0),
      itemsReturned: Math.trunc(entries[i].items_returned ?? 0),
      itemsReturnedSplit: {
        returned_refunds: Math.trunc(
          entries[i].items_returned_split?.items_returned_refund ?? 0
        ),
        returned_gift_cards: Math.trunc(
          entries[i].items_returned_split?.items_returned_store_credit ?? 0
        ),
        returned_exchanges: Math.trunc(
          entries[i].items_returned_split?.items_returned_exchange ?? 0
        )
      },
      returnRate: Math.trunc(entries[i].return_rate ?? 0),
      refundRate: Math.trunc(entries[i].refund_rate ?? 0),
      rdv: Math.trunc(entries[i].rdv ?? 0),
      shopCurrency: entries[i].currency_shop ?? 'EUR'
    })
  }
  return countryReturnRates
}

export function toReturnMetrics(
  response: DashboardanalyticspbReturnsMainViewResponse
): ReturnMetrics {
  const totalCompensation = calculateTotalCompensation(
    response.refunds_types ?? {}
  )
  return {
    totalReturns: response.returns ?? 0,
    totalReturnedItems: response.items ?? 0,
    rdvPercentage: response.rdv_perc ? formatPercentage(response.rdv_perc) : 0,
    rdvAmount: formatAnalyticsPrice(
      response.rdv ?? 0,
      response.money_format ?? {}
    ),
    totalCompensation: formatAnalyticsPrice(
      totalCompensation,
      response.money_format ?? {}
    ),
    compensationAmounts: calculateCompensationAmounts(
      response.refunds_types ?? {},
      totalCompensation,
      response.money_format ?? {}
    )
  }
}

function calculateTotalCompensation(
  refundTypes: DashboardanalyticspbRefundsTypes
): number {
  return (
    (refundTypes.bank_transfer_refund_total ?? 0) +
    (refundTypes.exchange_refund_total ?? 0) +
    (refundTypes.original_pm_refund_total ?? 0) +
    (refundTypes.store_credit_refund_total ?? 0)
  )
}

function formatPercentage(value: number) {
  return value * 100
}

function calculateCompensationAmounts(
  refundTypes: DashboardanalyticspbRefundsTypes,
  totalCompensation: number,
  mf: DashboardanalyticspbMoneyFormat
): CompensationAmounts {
  const bankTransferAmount = formatAnalyticsPrice(
    refundTypes.bank_transfer_refund_total ?? 0,
    mf
  )
  const bankTransferPercentage =
    totalCompensation === 0
      ? 0
      : ((refundTypes.bank_transfer_refund_total ?? 0) / totalCompensation) *
        100

  const originalPaymentMethodAmount = formatAnalyticsPrice(
    refundTypes.original_pm_refund_total ?? 0,
    mf
  )
  const originalPaymentMethodPercentage =
    totalCompensation === 0
      ? 0
      : ((refundTypes.original_pm_refund_total ?? 0) / totalCompensation) * 100

  const storeCreditAmount = formatAnalyticsPrice(
    refundTypes.store_credit_refund_total ?? 0,
    mf
  )
  const storeCreditPercentage =
    totalCompensation === 0
      ? 0
      : ((refundTypes.store_credit_refund_total ?? 0) / totalCompensation) * 100

  const exchangesAmount = formatAnalyticsPrice(
    refundTypes.exchange_refund_total ?? 0,
    mf
  )
  const exchangesPercentage =
    totalCompensation === 0
      ? 0
      : ((refundTypes.exchange_refund_total ?? 0) / totalCompensation) * 100

  const oneOnOneExchangesAmount = formatAnalyticsPrice(
    refundTypes.one_one_exchange_refund_total ?? 0,
    mf
  )
  const oneOnOneExchangesPercentage =
    totalCompensation === 0
      ? 0
      : ((refundTypes.one_one_exchange_refund_total ?? 0) / totalCompensation) *
        100

  const fullCatalogExchangesAmount = formatAnalyticsPrice(
    refundTypes.full_exchange_refund_total ?? 0,
    mf
  )
  const fullCatalogExchangesPercentage =
    totalCompensation === 0
      ? 0
      : ((refundTypes.full_exchange_refund_total ?? 0) / totalCompensation) *
        100

  return {
    bankTransfer: {
      amount: bankTransferAmount,
      percentage: bankTransferPercentage
    },
    originalPaymentMethod: {
      amount: originalPaymentMethodAmount,
      percentage: originalPaymentMethodPercentage
    },
    storeCredit: {
      amount: storeCreditAmount,
      percentage: storeCreditPercentage
    },
    exchanges: {
      amount: exchangesAmount,
      percentage: exchangesPercentage
    },
    oneOnOneExchanges: {
      amount: oneOnOneExchangesAmount,
      percentage: oneOnOneExchangesPercentage
    },
    fullCatalogExchanges: {
      amount: fullCatalogExchangesAmount,
      percentage: fullCatalogExchangesPercentage
    }
  }
}

export function toPeriodReturns(
  data: DashboardanalyticspbGetItemsAndTotalsPerDayResponse
): PeriodReturn[] {
  return (
    data.items_and_totals_days?.map((val) => {
      return {
        period: moment(val.date, 'YYYY-MM-DD').format('DD MMM') ?? '',
        totalReturns: val.returns_total_amount ?? 0,
        totalReturnedItems: val.item_count ?? 0
      }
    }) ?? []
  )
}

export function toCountryReturns(
  data: DashboardanalyticspbReturnsCountryResponse
): CountryReturns[] {
  return (
    data.return_countries?.map((val) => {
      return {
        totalReturns: val.returns_count ?? 0,
        percentage: Math.round(val.percentage ?? 0),
        country: val.country ?? ''
      }
    }) ?? []
  )
}

export function toProductMetrics(
  data: DashboardanalyticspbProductMainViewResponse
): { products: AnalyticsProduct[]; reasons: AnalyticsReturnReason[] } {
  const products = toAnalyticsProducts(data.product_analytics?.products ?? [])
  const reasons = toAnalyticsReturnReasons(
    data.product_analytics?.reasons ?? []
  )
  return { products, reasons }
}

function toAnalyticsProducts(
  prods: DashboardanalyticspbAnalyzedProduct[]
): AnalyticsProduct[] {
  return prods.map((prod) => {
    return {
      id: prod.product_id ?? '',
      reference: prod.product_reference ?? '',
      name: prod.name ?? '',
      imageURL: prod.image_url ?? '',
      totalReturns: prod.return_count ?? 0
    }
  })
}

function toAnalyticsReturnReasons(
  reasons: DashboardanalyticspbReason[]
): AnalyticsReturnReason[] {
  return reasons.map((reason) => {
    return {
      key: reason.name ?? '',
      totalReturnedItems: reason.items_count ?? 0,
      percentage: reason.percentage ?? 0
    }
  })
}

export function toProductsReturnRate(
  data: DashboardanalyticspbReturnRateTableResponse
): {
  productsReturnRate: ProductReturnRate[]
  totalEntries: number
} {
  const productsReturnRate: ProductReturnRate[] =
    data.entries?.map((e) => {
      return {
        id: e.product_id ?? '',
        reference: e.product_reference ?? '',
        name: e.product_name ?? '',
        totalPurchases: e.items_sold ?? 0,
        totalReturns: e.items_returned ?? 0,
        returnRate: e.return_rate ?? 0
      }
    }) ?? []

  return { productsReturnRate, totalEntries: data.total_entries ?? 0 }
}

export function toProducts(data: DashboardanalyticspbFindProductsResponse): {
  products: AnalyticsProduct[]
  totalDistinctReturnedProducts: number
} {
  const products = toAnalyticsProducts(data.products ?? [])
  const totalDistinctReturnedProducts =
    data.total_distinct_returned_products ?? 0
  return { products, totalDistinctReturnedProducts }
}

export function toProductDetails(
  data: DashboardanalyticspbProductDetailViewResponse
): {
  product: AnalyticsProduct
  reasons: AnalyticsReturnReason[]
  variantBreakdown: VariantReturnBreakdown[]
  productProcesses: ProductProcess[]
  dailyReturns: PeriodReturn[]
} {
  const product = toAnalyticsProduct(data.product_analytics?.product)
  const reasons = toAnalyticsReturnReasons(
    data.product_analytics?.reasons ?? []
  )
  const variantBreakdown = toVariantReturnBreakdown(
    data.product_analytics?.variant_breakdowns
  )
  const productProcesses = toProductProcesses(
    data.product_analytics?.product_processes ?? []
  )
  const dailyReturns = toDailyReturns(
    data.product_analytics?.return_count_per_days ?? []
  )
  return {
    product,
    reasons,
    variantBreakdown,
    productProcesses,
    dailyReturns
  }
}

function toAnalyticsProduct(
  prod?: DashboardanalyticspbAnalyzedProduct
): AnalyticsProduct {
  if (!prod)
    return {
      id: '',
      reference: '',
      name: '',
      imageURL: '',
      totalReturns: 0
    }
  return {
    id: prod.product_id ?? '',
    reference: prod.product_reference ?? '',
    name: prod.name ?? '',
    imageURL: prod.image_url ?? '',
    totalReturns: prod.return_count ?? 0
  }
}

function toVariantReturnBreakdown(
  variants?: DashboardanalyticspbVariantBreakdowns[]
): VariantReturnBreakdown[] {
  if (!variants) return []
  return variants.map((variant) => {
    return {
      name: variant.variant_name ?? '',
      totalReturns: variant.return_count ?? 0
    }
  })
}

function toProductProcesses(
  processes: DashboardanalyticspbProductProcesses[]
): ProductProcess[] {
  return processes.map((p) => {
    return {
      id: p.process_id ?? '',
      orderNumber: p.customer_printed_order_id ?? '',
      returnDate: moment(p.started, 'YYYY-MM-DD').format('DD MMM') ?? '',
      quantity: p.quantity ?? 0,
      returnReason: p.reason ?? '',
      variantName: p.variant_name ?? ''
    }
  })
}

function toDailyReturns(
  data: DashboardanalyticspbReturnCountPerDay[]
): PeriodReturn[] {
  return data.map((val) => {
    return {
      period: moment(val.date, 'YYYY-MM-DD').format('DD MMM') ?? '',
      totalReturns: val.return_count ?? 0
    }
  })
}

export function toProductReturnRate(
  data: DashboardanalyticspbAggregatedProductReturnRate[]
): PeriodReturnRate[] {
  return data.map((val) => {
    return {
      period: moment(val.period, 'YYYY-MM').format('MMM') ?? '',
      itemsPurchased: val.items_sold ?? 0,
      itemsReturned: val.items_returned ?? 0,
      returnRate: val.return_rate ?? 0
    }
  })
}

export function toProductCountryReturnRate(
  data: DashboardanalyticspbProductCountryReturnRate[]
): CountryReturnRate[] {
  return data.map((val) => ({
    country: val.country ?? '',
    itemsPurchased: Math.trunc(val.items_sold ?? 0),
    itemsReturned: Math.trunc(val.items_returned ?? 0),
    returnRate: val.return_rate ?? 0
  }))
}

export function toFilters(
  data: DashboardanalyticspbGetAvailableFiltersResponse
): Filter[] {
  if (!data) return []
  const filters: Filter[] = []
  if (data.countries) filters.push({ countries: data.countries })
  if (data.collections) filters.push({ collections: data.collections })
  return filters
}

export interface ScopePortal {
  id: string
  platform: string
  isDemo: boolean
}

export function toScopePortals(
  portals: DashboardanalyticspbScopePortal[]
): ScopePortal[] {
  return portals
    .filter((p) => p.ecommerce_id)
    .map((p) => ({
      id: p.ecommerce_id ?? '',
      platform: p.platform ?? '',
      isDemo: p.is_demo ?? false
    }))
}
